import { motion, useReducedMotion } from 'motion/react';
import { Fragment, memo, type ReactNode, useEffect, useState } from 'react';

import { useTrackAgentOnboardingSpotlightShown } from '@air/analytics';

import { ONBOARDING_INTRO_IMAGE_DIR } from '~/components/v2/components/workspace/onboarding-intro/constants/onboardingIntro';
import { PrivateWorkspaceAgentOnboardingAssetStack } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentOnboardingAssetStack';
import { PrivateWorkspaceAgentOnboardingTryIt } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentOnboardingTryIt';
import { WorkspaceAgentPanelToolGroupLoader } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/WorkspaceAgentPanelToolGroupLoader';
import { WorkspaceAgentPanelPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelPill';
import {
  WORKSPACE_AGENT_ONBOARDING_LIVE_AGENT_PROMPT_BY_TIME_SINK_ID,
  type WorkspaceAgentOnboardingRole,
  type WorkspaceAgentOnboardingTimeSinkId,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentOnboardingHandoff';
import { WorkspaceAgentBubble } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/bubble/WorkspaceAgentBubble';
import { type WorkspaceAgentResultBullet } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/messages/types';
import { WorkspaceAgentResultMessage } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/messages/WorkspaceAgentResultMessage';
import { type WorkspaceAgentPhaseListItem } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/types';
import { WorkspaceAgentLoadingDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/WorkspaceAgentLoadingDisclosure';
import { getWorkspaceAgentOnboardingWinningTimeSink } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentOnboardingWinningTimeSink';
import { splitOnboardingAgentAssetNames } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitOnboardingAgentAssetNames';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

const RUN_MS = 2800;

/**
 * Try-it follows the handoff beats: reply +1s pause, +1s sky lift (`HANDOFF_REVEAL_MS`), then this
 * (no extra pause).
 */
const TRY_IT_AFTER_SKY_MS = 0;

const PHASE_STEP_MS = 620;

const BOARD_ASSET_COUNT = 20;

const BOARD_ASSET_PHRASE = `the ${BOARD_ASSET_COUNT} assets on this board`;

const ROLE_ARTICLE: Partial<Record<WorkspaceAgentOnboardingRole, string>> = {
  Creative: 'a',
  'Founder / Executive': 'a',
  Marketing: 'in',
  'Product Design': 'in',
};

const still = (fileName: string) => `${ONBOARDING_INTRO_IMAGE_DIR}/${fileName}`;

const BOARD_STACK_STILLS = [
  still('library-flatlay.jpg'),
  still('library-jar-portrait.jpg'),
  still('library-hand-cream.jpg'),
  still('library-palette.jpg'),
];

/** Last entry is the front card; `totalCount` on the stack can exceed visible cards. */
const VARIANT_OUTPUT_STILLS = [
  still('resize-reel.webp'),
  still('resize-square.webp'),
  still('resize-portrait.webp'),
  still('resize-landscape.jpg'),
];

const HERO_STILL = still('resize-landscape.jpg');

const STORY_STILL = still('resize-reel.webp');

/** File name in onboarding copy → the still the chip shows. A missing name still chips, without a photo. */
const ONBOARDING_ASSET_THUMBNAIL_BY_NAME: Record<string, string> = {
  'AIRE_FW26_blush_hero_v3.jpg': HERO_STILL,
  'DSC_0192.jpg': still('library-jar-portrait.jpg'),
  'IMG_4821.jpg': still('library-flatlay.jpg'),
  'Screenshot.png': still('library-palette.jpg'),
  'aire_blush_cheek.jpg': still('library-cheek-tube.jpg'),
  'aire_blush_closeup.jpg': still('library-blush-closeup.jpg'),
  'aire_blush_cream.jpg': still('library-blush-cream.jpg'),
  'aire_blush_recline.jpg': still('library-blush-recline.jpg'),
  'aire_blush_story_FINAL_v2.jpg': STORY_STILL,
  'aire_fw26_email_hero.jpg': still('library-flatlay.jpg'),
  'aire_fw26_email_module_03.jpg': still('library-palette.jpg'),
  'aire_fw26_email_product.jpg': still('library-jar-portrait.jpg'),
};

/** `<File name>` in a mocked reply renders as an asset pill. Other text stays as written. */
const renderOnboardingAgentMessage = (message: string) =>
  splitOnboardingAgentAssetNames(message).map((part) =>
    part.type === 'text' ? (
      <Fragment key={part.key}>{part.value}</Fragment>
    ) : (
      <WorkspaceAgentPanelPill
        data-testid="WORKSPACE_AGENT_ONBOARDING_ASSET_PILL"
        key={part.key}
        label={part.name}
        thumbnailUrl={ONBOARDING_ASSET_THUMBNAIL_BY_NAME[part.name] ?? null}
      />
    ),
  );

const EMAIL_RENAME_LINES = [
  '<IMG_4821.jpg> is now <aire_fw26_email_hero.jpg>',
  '<DSC_0192.jpg> is now <aire_fw26_email_product.jpg>',
  '<Screenshot.png> is now <aire_fw26_email_module_03.jpg>',
];

const STATUS_RUNNING_PHASES: WorkspaceAgentPhaseListItem[] = [
  { id: 'read-scope', label: 'Read selection', status: 'running' },
  { id: 'analyze', label: 'Analyze', status: 'pending' },
  { id: 'present', label: 'Present summary', status: 'pending' },
];

const VARIANTS_RUNNING_PHASES: WorkspaceAgentPhaseListItem[] = [
  { id: 'making-sizes', label: 'Making more sizes', status: 'running' },
];

const RENAME_RUNNING_PHASES: WorkspaceAgentPhaseListItem[] = [
  { id: 'read-names', label: 'Reading file names', status: 'running' },
  { id: 'rename', label: 'Renaming for the email send', status: 'pending' },
];

const TAG_RUNNING_PHASES: WorkspaceAgentPhaseListItem[] = [
  { id: 'read-comments', label: 'Reading comments', status: 'running' },
  { id: 'check-approvals', label: 'Checking what is resolved', status: 'pending' },
  { id: 'apply-tags', label: 'Applying tags', status: 'pending' },
];

const FIND_RUNNING_PHASES: WorkspaceAgentPhaseListItem[] = [
  { id: 'search', label: 'Searching the library', status: 'running' },
  { id: 'approved', label: 'Checking approved', status: 'pending' },
  { id: 'favorites', label: 'Checking favorites', status: 'pending' },
];

const RESIZE_RUNNING_PHASES: WorkspaceAgentPhaseListItem[] = [
  { id: 'find-top-ad', label: 'Finding your top ad', status: 'running' },
  { id: 'resize', label: 'Resizing for each platform', status: 'pending' },
];

const RUNNING_PHASES: Record<WorkspaceAgentOnboardingTimeSinkId, readonly WorkspaceAgentPhaseListItem[]> = {
  analytics: RESIZE_RUNNING_PHASES,
  collaboration: STATUS_RUNNING_PHASES,
  distribution: RENAME_RUNNING_PHASES,
  find: FIND_RUNNING_PHASES,
  generation: VARIANTS_RUNNING_PHASES,
  multiSelect: TAG_RUNNING_PHASES,
};

const STATUS_SETTLED_PHASES: WorkspaceAgentPhaseListItem[] = [
  {
    detail: 'Loaded review status, comments, and tags',
    id: 'read-scope',
    label: 'Read selection',
    status: 'done',
  },
  {
    detail: 'Checked open comments, approvals, and blockers',
    id: 'analyze',
    label: 'Analyze',
    status: 'done',
  },
  { detail: 'Drafted a status summary', id: 'present', label: 'Present summary', status: 'done' },
];

const STATUS_BULLETS: WorkspaceAgentResultBullet[] = [
  {
    id: 'comments',
    quotes: [
      { attribution: 'Elena Gafney', id: 'comment-crop', quote: 'Can we use a tighter crop?' },
      { attribution: 'Elena Gafney', id: 'comment-rights', quote: 'This one is great' },
    ],
    segments: [{ text: '2 unresolved comments need a response:', type: 'text' }],
    thumbnails: [
      { id: 'comment-crop', label: 'AIRE_FW26_blush_hero_v3.jpg', thumbnailUrl: HERO_STILL },
      { id: 'comment-rights', label: 'aire_blush_story_FINAL_v2.jpg', thumbnailUrl: STORY_STILL },
    ],
  },
];

const TAG_SETTLED_PHASES: WorkspaceAgentPhaseListItem[] = [
  { detail: 'Read every comment on the selection', id: 'read-comments', label: 'Read comments', status: 'done' },
  {
    detail: 'Sorted resolved threads from open ones',
    id: 'check-approvals',
    label: 'Check what is resolved',
    status: 'done',
  },
  { detail: 'Tagged Approved and Needs review', id: 'apply-tags', label: 'Apply tags', status: 'done' },
];

const TAG_BULLETS: WorkspaceAgentResultBullet[] = [
  {
    id: 'approved',
    segments: [
      { text: '18 are ready, so I tagged them', type: 'text' },
      { label: 'Approved', type: 'tag' },
    ],
  },
  {
    id: 'needs-review',
    segments: [
      { text: '2 still have an open comment, so I tagged them', type: 'text' },
      { label: 'Needs review', type: 'tag' },
    ],
    thumbnails: [
      { id: 'needs-review-hero', label: 'AIRE_FW26_blush_hero_v3.jpg', thumbnailUrl: HERO_STILL },
      { id: 'needs-review-story', label: 'aire_blush_story_FINAL_v2.jpg', thumbnailUrl: STORY_STILL },
    ],
  },
];

const FIND_SETTLED_PHASES: WorkspaceAgentPhaseListItem[] = [
  { detail: 'Searched the library for Annie Quinn', id: 'search', label: 'Search the library', status: 'done' },
  { detail: 'Grouped the ones tagged Approved', id: 'approved', label: 'Check approved', status: 'done' },
  { detail: 'Grouped the favorites', id: 'favorites', label: 'Check favorites', status: 'done' },
];

const FIND_BULLETS: WorkspaceAgentResultBullet[] = [
  {
    id: 'approved',
    segments: [
      { text: '8 are tagged', type: 'text' },
      { label: 'Approved', type: 'tag' },
    ],
    thumbnails: [
      {
        canOpen: false,
        id: 'find-approved-cheek',
        label: 'aire_blush_cheek.jpg',
        thumbnailUrl: still('library-cheek-tube.jpg'),
      },
      {
        canOpen: false,
        id: 'find-approved-closeup',
        label: 'aire_blush_closeup.jpg',
        thumbnailUrl: still('library-blush-closeup.jpg'),
      },
    ],
  },
  {
    id: 'favorited',
    segments: [{ text: '4 are favorited.', type: 'text' }],
    thumbnails: [
      {
        canOpen: false,
        id: 'find-favorite-cream',
        label: 'aire_blush_cream.jpg',
        thumbnailUrl: still('library-blush-cream.jpg'),
      },
      {
        canOpen: false,
        id: 'find-favorite-recline',
        label: 'aire_blush_recline.jpg',
        thumbnailUrl: still('library-blush-recline.jpg'),
      },
    ],
  },
];

const RESIZE_PLATFORMS = ['Instagram', 'Facebook', 'LinkedIn', 'TikTok', 'X', 'YouTube', 'Snapchat', 'Pinterest'];

type PromptSegment = { id: string; type: 'chip' | 'text'; value: string };

const spotlightTrackingKey = ({
  outcomeId,
  role,
  timeSinkIds,
}: {
  outcomeId: WorkspaceAgentOnboardingTimeSinkId;
  role: WorkspaceAgentOnboardingRole;
  timeSinkIds: readonly WorkspaceAgentOnboardingTimeSinkId[];
}) => `${role}|${timeSinkIds.join(',')}|${outcomeId}`;

/** Survives remounts when the agent panel view re-keys with the same handoff. */
let lastTrackedSpotlightKey: string | null = null;

const promptSegmentsFor = ({
  role,
  timeSinkLabel,
}: {
  role: WorkspaceAgentOnboardingRole;
  timeSinkLabel: string;
}): PromptSegment[] => {
  const article = role === 'Other' ? undefined : (ROLE_ARTICLE[role] ?? 'a');
  const opening: PromptSegment[] = article
    ? [
        { id: 'opening-article', type: 'text', value: `I'm ${article} ` },
        { id: 'opening-role', type: 'chip', value: role },
        { id: 'opening-time-preamble', type: 'text', value: ' spending a lot of time on ' },
      ]
    : [{ id: 'opening-time-preamble', type: 'text', value: "I'm spending a lot of time on " }];

  return [
    ...opening,
    { id: 'time-sink', type: 'chip', value: timeSinkLabel },
    { id: 'closing', type: 'text', value: '.' },
  ];
};

export type PrivateWorkspaceAgentOnboardingTimeSinksProps = {
  role: WorkspaceAgentOnboardingRole;
  timeSinkIds: readonly WorkspaceAgentOnboardingTimeSinkId[];
};

/**
 * The settled first-run thread. The highest-priority selected pill is named in the user line and
 * chooses which result plays.
 */
export const PrivateWorkspaceAgentOnboardingTimeSinks = memo(
  ({ role, timeSinkIds }: PrivateWorkspaceAgentOnboardingTimeSinksProps) => {
    const winningTimeSink = getWorkspaceAgentOnboardingWinningTimeSink(timeSinkIds);
    const runningPhases = winningTimeSink ? RUNNING_PHASES[winningTimeSink.id] : [];
    const shouldReduceMotion = !!useReducedMotion();
    const [runningIndex, setRunningIndex] = useState(0);
    const [isSettled, setIsSettled] = useState(shouldReduceMotion);
    const [showTryIt, setShowTryIt] = useState(shouldReduceMotion);
    const isOnboardingSkyDismissed = useWorkspaceAgentPanelStore((state) => state.isOnboardingSkyDismissed);
    const isHybridTurnLineVisible = useWorkspaceAgentPanelStore((state) => state.isOnboardingHybridTurnLineVisible);
    const setIsOnboardingReplyReady = useWorkspaceAgentPanelStore((state) => state.setIsOnboardingReplyReady);
    const liveAgentPrompt = winningTimeSink
      ? WORKSPACE_AGENT_ONBOARDING_LIVE_AGENT_PROMPT_BY_TIME_SINK_ID[winningTimeSink.id]
      : undefined;
    const { trackAgentOnboardingSpotlightShown } = useTrackAgentOnboardingSpotlightShown();

    useEffect(() => {
      if (!winningTimeSink) {
        return;
      }

      const key = spotlightTrackingKey({
        outcomeId: winningTimeSink.id,
        role,
        timeSinkIds,
      });

      if (lastTrackedSpotlightKey === key) {
        return;
      }

      lastTrackedSpotlightKey = key;
      trackAgentOnboardingSpotlightShown({
        outcome: winningTimeSink.id,
        resolved_preview_id: winningTimeSink.id,
      });
    }, [role, timeSinkIds, trackAgentOnboardingSpotlightShown, winningTimeSink]);

    useEffect(() => {
      if (!winningTimeSink) {
        return;
      }

      if (shouldReduceMotion) {
        setIsSettled(true);
        return;
      }

      setIsSettled(false);
      setRunningIndex(0);

      const finishedIndex = runningPhases.length;
      const stepTimer = window.setInterval(() => {
        setRunningIndex((current) => Math.min(current + 1, finishedIndex));
      }, PHASE_STEP_MS);
      const settleTimer = window.setTimeout(() => setIsSettled(true), RUN_MS);

      return () => {
        window.clearInterval(stepTimer);
        window.clearTimeout(settleTimer);
      };
    }, [runningPhases.length, shouldReduceMotion, winningTimeSink]);

    useEffect(() => {
      setIsOnboardingReplyReady(isSettled);
    }, [isSettled, setIsOnboardingReplyReady]);

    useEffect(() => {
      return () => {
        const panel = useWorkspaceAgentPanelStore.getState();

        if (panel.isElevatedAboveOnboarding && !panel.isOnboardingReplyReady) {
          panel.setIsOnboardingReplyReady(true);
        }
      };
    }, []);

    useEffect(() => {
      if (liveAgentPrompt) {
        setShowTryIt(false);
        return;
      }

      if (shouldReduceMotion) {
        setShowTryIt(true);
        return;
      }

      if (!isOnboardingSkyDismissed) {
        setShowTryIt(false);
        return;
      }

      const timeout = window.setTimeout(() => setShowTryIt(true), TRY_IT_AFTER_SKY_MS);

      return () => window.clearTimeout(timeout);
    }, [isOnboardingSkyDismissed, liveAgentPrompt, shouldReduceMotion]);

    if (!winningTimeSink) {
      return null;
    }

    const livePhases: WorkspaceAgentPhaseListItem[] = runningPhases.map((phase, index) => {
      if (index < runningIndex) {
        return { ...phase, status: 'done' };
      }

      if (index === runningIndex) {
        return { ...phase, status: 'running' };
      }

      return { ...phase, status: 'pending' };
    });
    const livePhase = livePhases.find((phase) => phase.status === 'running');
    let liveLabel = 'Finishing up…';

    if (winningTimeSink.id === 'generation') {
      liveLabel = 'Making more sizes…';
    } else if (livePhase) {
      liveLabel = `${livePhase.label}…`;
    }

    const segments = promptSegmentsFor({ role, timeSinkLabel: winningTimeSink.label });
    const showsBoardStack = winningTimeSink.id !== 'find';
    let opener = '';
    let result: ReactNode = null;

    if (winningTimeSink.id === 'collaboration') {
      opener = `Let me catch you up on the open comments. Reading ${BOARD_ASSET_PHRASE}.`;
      result = (
        <WorkspaceAgentResultMessage
          body={renderOnboardingAgentMessage(
            'Open comments are on <AIRE_FW26_blush_hero_v3.jpg> and <aire_blush_story_FINAL_v2.jpg>.',
          )}
          bodyTitle="Here's what needs attention on this board:"
          bullets={STATUS_BULLETS}
          work={{ label: 'Status ready', phases: STATUS_SETTLED_PHASES }}
        />
      );
    } else if (winningTimeSink.id === 'generation') {
      opener = `I can generate variants at scale. Let's make square and portrait crops for ${BOARD_ASSET_PHRASE}.`;
      result = (
        <WorkspaceAgentResultMessage
          body={renderOnboardingAgentMessage(`Done. I made square and portrait crops from ${BOARD_ASSET_PHRASE}.`)}
          detail={
            <PrivateWorkspaceAgentOnboardingAssetStack
              idPrefix="generation"
              thumbnailUrls={VARIANT_OUTPUT_STILLS}
              totalCount={BOARD_ASSET_COUNT * 2}
            />
          }
        />
      );
    } else if (winningTimeSink.id === 'distribution') {
      opener = `An email send is easier when the files are named for it. I'll rename ${BOARD_ASSET_PHRASE} for the email campaign.`;
      result = (
        <WorkspaceAgentResultMessage
          body={
            <div className="space-y-1">
              {EMAIL_RENAME_LINES.map((line) => (
                <p key={line}>{renderOnboardingAgentMessage(line)}</p>
              ))}
            </div>
          }
          bodyTitle={`Done. I renamed ${BOARD_ASSET_PHRASE} for the email send:`}
        />
      );
    } else if (winningTimeSink.id === 'multiSelect') {
      opener = `I can organize the whole board at once. I'll read the comments on ${BOARD_ASSET_PHRASE} and tag each one by where it stands.`;
      result = (
        <WorkspaceAgentResultMessage
          body={renderOnboardingAgentMessage(
            'Still open on <AIRE_FW26_blush_hero_v3.jpg> and <aire_blush_story_FINAL_v2.jpg>.',
          )}
          bodyTitle={`Done. I read every comment on ${BOARD_ASSET_PHRASE}:`}
          bullets={TAG_BULLETS}
          work={{ label: 'Tags updated', phases: TAG_SETTLED_PHASES }}
        />
      );
    } else if (winningTimeSink.id === 'find') {
      opener =
        "Let me help you prep on that campaign for Annie Quinn. I'll search through the library for her images and see which ones are approved or favorited.";
      result = (
        <WorkspaceAgentResultMessage
          body={renderOnboardingAgentMessage(
            'Including <aire_blush_cheek.jpg>, <aire_blush_closeup.jpg>, <aire_blush_cream.jpg>, and <aire_blush_recline.jpg>.',
          )}
          bodyTitle="Here's Annie Quinn's shortlist:"
          bullets={FIND_BULLETS}
          work={{ label: 'Search ready', phases: FIND_SETTLED_PHASES }}
        />
      );
    } else {
      const platformList = `${RESIZE_PLATFORMS.slice(0, -1).join(', ')}, and ${RESIZE_PLATFORMS.at(-1)}`;
      opener = `Let's see how your assets have been working. I can resize your highest performing ad for ${platformList}.`;
      result = (
        <WorkspaceAgentResultMessage
          body={renderOnboardingAgentMessage('Done. I resized <AIRE_FW26_blush_hero_v3.jpg> for all 8 platforms.')}
          detail={
            <PrivateWorkspaceAgentOnboardingAssetStack
              idPrefix="resize"
              thumbnailUrls={VARIANT_OUTPUT_STILLS}
              totalCount={RESIZE_PLATFORMS.length}
            />
          }
        />
      );
    }

    return (
      <div
        className="flex w-full min-w-0 flex-col gap-3 px-1 pt-5 pb-3"
        data-testid="WORKSPACE_AGENT_ONBOARDING_SIMULATION"
      >
        <WorkspaceAgentBubble variant="user">
          <p className="text-14 text-gray-12 leading-5">
            {segments.map((segment) =>
              segment.type === 'text' ? (
                <Fragment key={segment.id}>{segment.value}</Fragment>
              ) : (
                <WorkspaceAgentPanelPill
                  data-testid="WORKSPACE_AGENT_ONBOARDING_ANSWER_CHIP"
                  key={segment.id}
                  label={segment.value}
                  thumbnailUrl={null}
                />
              ),
            )}
          </p>
        </WorkspaceAgentBubble>
        {showsBoardStack ? (
          <div className="flex w-full justify-end">
            <PrivateWorkspaceAgentOnboardingAssetStack
              idPrefix="board"
              thumbnailUrls={BOARD_STACK_STILLS}
              totalCount={BOARD_ASSET_COUNT}
            />
          </div>
        ) : null}
        <WorkspaceAgentBubble variant="agent">
          <p className="break-words text-14 text-gray-12 leading-5">{renderOnboardingAgentMessage(opener)}</p>
        </WorkspaceAgentBubble>
        <div className="w-full min-w-0">
          {isSettled ? (
            result
          ) : (
            <WorkspaceAgentLoadingDisclosure
              defaultIsExpanded={winningTimeSink.id !== 'generation'}
              label={liveLabel}
              mark={<WorkspaceAgentPanelToolGroupLoader className="mt-0.5 size-6 shrink-0" />}
              phases={livePhases}
            />
          )}
        </div>
        {isHybridTurnLineVisible ? (
          <>
            <div
              aria-hidden
              className="pointer-events-none relative z-1 -mt-24 h-24 bg-gradient-to-t from-gray-1 via-gray-1/70 to-transparent"
              data-testid="WORKSPACE_AGENT_ONBOARDING_HYBRID_DEMO_FADE"
            />
            <motion.p
              animate={{ opacity: 1, y: 0 }}
              className="m-0 pt-5 pb-3 font-semibold text-14 text-gray-12 leading-5"
              data-testid="WORKSPACE_AGENT_ONBOARDING_HYBRID_TURN_LINE"
              data-workspace-agent-onboarding-turn-line=""
              initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            >
              <span className="sr-only">Agent said: </span>
              Now it&apos;s your turn
            </motion.p>
          </>
        ) : null}
        {showTryIt ? <PrivateWorkspaceAgentOnboardingTryIt /> : null}
      </div>
    );
  },
);

PrivateWorkspaceAgentOnboardingTimeSinks.displayName = 'PrivateWorkspaceAgentOnboardingTimeSinks';
